import { useState } from 'react'
import { translate, type Lang } from './i18n'
import { Tabs } from './Tabs'
import { TrendsView } from './Trends'
import type { BackfillResult, ShiftPerformance, ShiftReview, Trends , Denial} from './api'
import { DataNote } from './DataNote'
import { ReportView } from './Report'
import { agencyToday } from './time'

/**
 * INSIGHT: plan contra real (§11).
 *
 * Lo que sale de aquí es cumplimiento, no efectividad. Si el patrullaje bajó el delito es una
 * afirmación causal y §11.1 la sujeta a «metodología aprobada»; sin esa metodología acordada con
 * el departamento, publicar el número sería inventarse una conclusión. Esta pantalla contesta algo
 * más modesto y más comprobable: qué se planeó, qué se hizo y qué quedó sin hacer.
 */
export function InsightScreen({
  lang, shifts, review, trends, busy, demo, canRebuild, rebuilt, onOpen, onRebuild, onExport, loaded, problem,
  agency, user,
}: {
  lang: Lang
  shifts: ShiftPerformance[]
  review: ShiftReview | null
  trends: Trends | null
  busy: boolean
  /** En modo demostración los turnos los ejecutó el simulador, y eso cambia qué significan. */
  demo: boolean
  /** Rehacer la historia borra y reconstruye: es mantenimiento, no lectura. */
  canRebuild: boolean
  rebuilt: BackfillResult | null
  onOpen: (date: string, code: string) => void
  onRebuild: () => void
  onExport: () => void
  loaded: boolean
  problem: Denial | null
  /** Para el encabezado del reporte: de quién es y quién lo sacó. */
  agency: string
  user: string
}) {
  const [abierto, setAbierto] = useState<string | null>(null)
  const [reporte, setReporte] = useState(false)

  // El reporte ocupa la pantalla entera y esconde lo demás. Es a propósito: lo que se ve es lo
  // que va a salir impreso, sin que haya que adivinar qué se lleva el papel.
  if (reporte && trends) {
    return (
      <div className="layout single">
        <div className="side full">
          <div className="row-line print-hide" style={{ marginBottom: 12 }}>
            <button onClick={() => setReporte(false)}>{translate(lang, 'report.close')}</button>
            <button onClick={() => window.print()}>{translate(lang, 'report.print')}</button>
          </div>
          <ReportView
            lang={lang} agency={agency} user={user} trends={trends} shifts={shifts}
            issued={agencyToday()}
          />
        </div>
      </div>
    )
  }

  const pct = (v: number | null) => (v === null ? '—' : `${Math.round(v * 100)}%`)
  const min = (v: number) => `${Math.round(v)} min`

  const desvios = (s: ShiftPerformance) =>
    s.lateArrivals + s.shortPresence + s.outOfZone + s.connectivityLost + (s.shiftsNotStarted ?? 0) + (s.outsideJurisdiction ?? 0)

  return (
    <div className="layout single">
      <aside className="side full">
        <p className="notice">{translate(lang, 'insight.help')}</p>

        {/* Que no se lea un número sin saber de dónde salió. */}
        <p className={demo ? 'error' : 'notice'}>
          {translate(lang, demo ? 'insight.simulated' : 'insight.caveat')}
        </p>

        {/*
          Dos preguntas distintas: «¿cómo venimos?» se contesta con el periodo entero y «¿cómo nos
          fue?» con un turno. Mezclarlas en una sola columna hacía que la segunda tapara a la
          primera, que es la que cambia decisiones.
        */}
        <Tabs
          area="insight"
          tabs={[
            {
              id: 'tendencias',
              label: translate(lang, 'insight.tabTrends'),
              content: (
                <TrendsView
                  lang={lang} trends={trends} busy={busy} onExport={onExport}
                  onReport={() => setReporte(true)}
                />
              ),
            },
            {
              id: 'turnos',
              label: translate(lang, 'insight.tabShifts'),
              content: (
                <>
            {canRebuild && (
              <div className="card">
                <p className="notice">{translate(lang, 'insight.rebuildHelp')}</p>
                <div className="actions">
                  <button disabled={busy} onClick={onRebuild}>
                    {translate(lang, 'insight.rebuild')}
                  </button>
                  {rebuilt && (
                    <span className="meta">
                      {translate(lang, 'insight.rebuilt', [rebuilt.shifts, rebuilt.applied])}
                    </span>
                  )}
                </div>
              </div>
            )}

            <section className="panel">
              <h2>{translate(lang, 'insight.shifts')}</h2>
              <DataNote lang={lang} loaded={loaded} problem={problem} count={shifts.length} empty="insight.empty" />
              {shifts.length === 0 && loaded && problem === null ? (
                <div className="card">
                  <p className="notice">{translate(lang, 'insight.empty')}</p>
                  {canRebuild && <p className="notice">{translate(lang, 'insight.emptyRebuild')}</p>}
                </div>
              ) : (
                <div className="card">
                  <table className="compare">
                    <thead>
                      <tr>
                        <th>{translate(lang, 'insight.shift')}</th>
                        <th>{translate(lang, 'insight.planned')}</th>
                        <th>{translate(lang, 'insight.released')}</th>
                        <th>{translate(lang, 'insight.completed')}</th>
                        <th>{translate(lang, 'insight.missed')}</th>
                        <th>{translate(lang, 'insight.deferred')}</th>
                        <th>{translate(lang, 'insight.onTime')}</th>
                        <th>{translate(lang, 'insight.presence')}</th>
                        <th>{translate(lang, 'insight.deviations')}</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {shifts.map((s) => {
                        const id = `${s.shiftDate}/${s.shiftCode}`
                        return (
                          <tr key={id} className={abierto === id ? 'chosen' : ''}>
                            <td>{s.shiftDate} · {s.shiftCode}</td>
                            <td className="num">{s.plannedAssignments}</td>
                            <td className="num">{s.released}</td>
                            <td className="num">{s.completed}</td>
                            <td className={(s.missed ?? 0) > 0 ? 'num error' : 'num'}>{s.missed ?? 0}</td>
                            <td className={s.deferred > 0 ? 'num error' : 'num'}>{s.deferred}</td>
                            <td className="num">{pct(s.onTimeRate)}</td>
                            <td className="num">{pct(s.presenceRate)}</td>
                            <td className="num">{desvios(s)}</td>
                            <td>
                              <button
                                disabled={busy}
                                onClick={() => { setAbierto(id); onOpen(s.shiftDate, s.shiftCode) }}
                              >
                                {translate(lang, 'insight.open')}
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {review && (
              <section className="panel">
                <h2>
                  {translate(lang, 'insight.review', [review.shift.shiftDate, review.shift.shiftCode])}
                </h2>
                <p className="notice">{translate(lang, 'insight.reviewHelp')}</p>

                <div className="grid">
                <div className="card">
                  <div className="meta">
                    {translate(lang, 'insight.approvedBy', [review.shift.approvedBy || '—'])}
                    {review.shift.isPartial && ` · ${translate(lang, 'insight.partial')}`}
                  </div>
                  <div className="meta">
                    {translate(lang, 'insight.resources', [
                      review.shift.officersAvailable,
                      review.shift.officersAbsent,
                      review.shift.officersRestricted,
                      review.shift.vehiclesAvailable,
                    ])}
                  </div>
                  <div className="meta">
                    {translate(lang, 'insight.deviationBreakdown', [
                      review.shift.lateArrivals,
                      review.shift.shortPresence,
                      review.shift.outOfZone,
                      review.shift.connectivityLost,
                      review.shift.shiftsNotStarted ?? 0,
                      review.shift.outsideJurisdiction ?? 0,
                    ])}
                  </div>
                  {((review.shift.supportRequests ?? 0) + (review.shift.planVersionsReturned ?? 0) + (review.shift.manualAdjustments ?? 0)) > 0 && (
                    <div className="meta">
                      {translate(lang, 'insight.commandActivity', [
                        review.shift.supportRequests ?? 0,
                        review.shift.planVersionsReturned ?? 0,
                        review.shift.manualAdjustments ?? 0,
                      ])}
                    </div>
                  )}
                  {(review.shift.scenariosGenerated ?? 0) > 0 && (
                    <div className="meta">
                      {translate(lang, 'insight.scenariosGenerated', [review.shift.scenariosGenerated ?? 0])}
                    </div>
                  )}
                  {review.shift.reoptimizationRuns > 0 && (
                    <div className="meta">
                      {translate(lang, 'insight.reoptimizations', [
                        review.shift.reoptimizationRuns, review.shift.reoptimizationsProposed,
                      ])}
                    </div>
                  )}
                  {/*
                    Las dos mitades de la pregunta que §11.1 hace de la reoptimización:
                    ¿respondió a tiempo? y ¿sirvió de algo? Van juntas porque una sin la otra
                    engaña: un motor rapidísimo que no recupera nada no ayudó a nadie.
                  */}
                  {review.shift.reoptimizationAverageMs !== null && (
                    <div className="meta">
                      {translate(lang, 'insight.reoptimizationCost', [
                        Math.round(review.shift.reoptimizationAverageMs),
                      ])}
                      {review.shift.coverageRecovered !== null && ' · '}
                      {review.shift.coverageRecovered !== null &&
                        translate(lang, 'insight.coverageRecovered', [
                          review.shift.coverageRecovered >= 0 ? '+' : '',
                          review.shift.coverageRecovered.toFixed(1),
                        ])}
                    </div>
                  )}
                </div>

                <div className="card">
                  <h3>{translate(lang, 'insight.units')}</h3>
                  <table className="compare">
                    <thead>
                      <tr>
                        <th>{translate(lang, 'insight.unit')}</th>
                        <th>{translate(lang, 'insight.assignments')}</th>
                        <th>{translate(lang, 'insight.completed')}</th>
                        <th>{translate(lang, 'insight.missed')}</th>
                        <th>{translate(lang, 'insight.late')}</th>
                        <th>{translate(lang, 'insight.stay')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {review.units.map((u) => (
                        <tr key={u.patrolUnitId}>
                          <td>{u.callSign}</td>
                          <td className="num">{u.assignments}</td>
                          <td className="num">{u.completed}</td>
                          <td className={(u.missed ?? 0) > 0 ? 'num error' : 'num'}>{u.missed ?? 0}</td>
                          <td className="num">
                            {u.averageLateMinutes === null ? '—' : min(u.averageLateMinutes)}
                          </td>
                          <td className="num">{min(u.presence)} / {min(u.planned)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {review.incidents.length > 0 && (
                  <div className="card">
                    <h3>{translate(lang, 'insight.incidents')}</h3>
                    <p className="notice">{translate(lang, 'insight.incidentsHelp')}</p>
                    <div className="rows">
                      {review.incidents.map((i) => (
                        <div key={i.incidentId} className="row-line">
                          <span className="meta">{i.callSign}</span>
                          <span className="grow">{i.description}</span>
                          <span className="meta">
                            {i.minutes === null ? translate(lang, 'insight.open2') : min(i.minutes)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                </div>
              </section>
            )}
                </>
              ),
            },
          ]}
        />
      </aside>
    </div>
  )
}
